/**
 * O30-3 (#1818): admin-ops「AI 利用」画面。
 *
 * 運営が AI に「いくら・何に・どの顧客で」使っているかを、期間を選んで読む面。毎朝 09:00 JST の
 * 運営 Slack 日次レポート（「admin-ops で見る」→ `?period=yesterday`）の着地先で、同じ関数・同じ定義で数える。
 * **見る面であって、止める・変える面ではない**（操作 UI を作らない）。
 *
 * 正本: `spec/sales-counter/screen-contracts/admin-ops-ai-usage.md`（塊 1〜4）/ State Matrix 同名 /
 * Experience Spec の U 群（文言）。
 *
 * 守るライン:
 *   - #1: 個別の呼び出し・本文・リード名を出さない。行から明細を開く導線を作らない。
 *   - #2: 概算原価は運営内部の数字。顧客の料金・プラン・上限を出さない（「請求」の語は U-2 の否定文だけ）。
 *
 * PC 専用（min-width: 1024px、他の admin-ops 画面と同じ）。
 */
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  type AiUsageConfigured,
  AiUsageFetchError,
  type AiUsagePeriod,
  fetchAiUsage,
} from '../../lib/ai-usage-api';
import { AiUsageBreakdown, type Axis, type SortKey, workspaceLabel } from './AiUsageBreakdown';
import { formatInt, formatJpy, formatMTok, formatUsd, jstDate, jstTime } from './format';

export interface AiUsagePageProps {
  apiBaseUrl: string;
}

const PERIODS: Array<[AiUsagePeriod, string]> = [
  ['today', '今日'],
  ['yesterday', '昨日'],
  ['last7d', '7 日'],
  ['this_month', '今月'],
  ['last_month', '先月'],
];
const AXES: Array<[Axis, string]> = [
  ['feature', '機能別'],
  ['workspace', 'workspace 別'],
  ['model', 'モデル別'],
];

const isPeriod = (v: string | null): v is AiUsagePeriod => PERIODS.some(([p]) => p === v);
const isAxis = (v: string | null): v is Axis => AXES.some(([a]) => a === v);

function Tile({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <p className="text-xs text-slate-500">{title}</p>
      <div className="mt-1 tabular-nums">{children}</div>
    </div>
  );
}

function Totals({ data, filterName }: { data: AiUsageConfigured; filterName: string | null }) {
  const t = data.total;
  const allUnpriced = t.calls > 0 && t.unpricedCalls === t.calls;
  return (
    <div data-testid="ai-usage-totals">
      {filterName && <p className="mb-2 text-xs text-slate-600">{filterName} のみ</p>}
      <div className="grid grid-cols-4 gap-4">
        <Tile title="概算原価">
          <p className="text-2xl font-semibold text-slate-900">
            {allUnpriced ? (
              <span className="text-amber-700">単価未登録</span>
            ) : (
              formatUsd(t.costUsd)
            )}
          </p>
          {!allUnpriced && (
            <p className="text-xs text-slate-500">{formatJpy(t.costUsd * data.rate.rate)}</p>
          )}
          {/* 一部でも全部でも、単価未登録の件数は合計の真下に出す（P3 / P4）。 */}
          {t.unpricedCalls > 0 && (
            <p className="mt-1 text-xs text-amber-700">
              単価未登録の {formatInt(t.unpricedCalls)} 件を含みません（モデル別で確認）
            </p>
          )}
        </Tile>
        <Tile title="呼び出し件数">
          <p className="text-2xl font-semibold text-slate-900">{formatInt(t.calls)} 件</p>
        </Tile>
        <Tile title="入力トークン">
          <p className="text-2xl font-semibold text-slate-900" title={formatInt(t.inputTokens)}>
            {formatMTok(t.inputTokens)}
          </p>
        </Tile>
        <Tile title="出力トークン">
          <p className="text-2xl font-semibold text-slate-900" title={formatInt(t.outputTokens)}>
            {formatMTok(t.outputTokens)}
          </p>
        </Tile>
      </div>
    </div>
  );
}

/** 塊 4。API の値が要らない行（成功した呼び出しのみ）は読み込み前から出す（Screen Contract「読込中」）。 */
function Notes({ data }: { data: AiUsageConfigured | null }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="mt-8 space-y-1 text-xs text-slate-600">
      {data && data.revisionsInPeriod.length > 0 && (
        <p className="text-amber-700">
          この期間の途中（{data.revisionsInPeriod.join('・')}
          ）に単価が改定されています。金額は呼び出し時点の単価で計算しています。
        </p>
      )}
      {data && (
        <>
          <p>単価: {data.latestRevision ?? '—'} 改定の公開単価（USD / 100 万トークン）</p>
          <p>
            円換算: 1 USD = ¥{data.rate.rate}（固定・{data.rate.asOf} 設定）
          </p>
        </>
      )}
      <p>成功した呼び出しのみを数えています。失敗した呼び出しの原価は含まれません。</p>
      {data && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="mt-2 text-slate-700 underline"
        >
          単価表を見る
        </button>
      )}
      {data && open && (
        <table data-testid="ai-usage-price-table" className="mt-2 w-auto">
          <thead>
            <tr className="text-left text-slate-500">
              <th className="pr-6 font-medium">モデル</th>
              <th className="pr-6 font-medium">提供元</th>
              <th className="pr-6 text-right font-medium">入力</th>
              <th className="pr-6 text-right font-medium">出力</th>
              <th className="font-medium">改定日</th>
            </tr>
          </thead>
          <tbody className="tabular-nums">
            {data.prices.map((p) => (
              <tr key={`${p.model}/${p.effectiveFrom}`}>
                <td className="pr-6 font-mono">{p.model}</td>
                <td className="pr-6">{p.provider}</td>
                <td className="pr-6 text-right">{formatUsd(p.inputUsdPerMTok)}</td>
                <td className="pr-6 text-right">{formatUsd(p.outputUsdPerMTok)}</td>
                <td>{p.effectiveFrom}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

export function AiUsagePage({ apiBaseUrl }: AiUsagePageProps) {
  const [params, setParams] = useSearchParams();
  const period: AiUsagePeriod = isPeriod(params.get('period'))
    ? (params.get('period') as AiUsagePeriod)
    : 'last7d';
  const axis: Axis = isAxis(params.get('axis')) ? (params.get('axis') as Axis) : 'feature';
  const sort: SortKey = params.get('sort') === 'calls' ? 'calls' : 'cost';
  // 絞り込みの URL クエリ名は State Matrix D5 の `workspace=`。
  const ws = params.get('workspace');

  const update = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null) next.delete(k);
      else next.set(k, v);
    }
    setParams(next);
  };

  const query = useQuery({
    queryKey: ['admin-ops-ai-usage', period, ws],
    queryFn: () => fetchAiUsage(apiBaseUrl, { period, workspaceId: ws }),
    // 期間・絞り込みの切替中は直前の数字を残す（一瞬 0 や空に見せない）。
    placeholderData: keepPreviousData,
    retry: false,
  });

  const data = query.data;
  const configured = data?.configured ? data : null;
  const switching = query.isPlaceholderData && query.isFetching;
  const errorKind =
    query.error instanceof AiUsageFetchError ? query.error.kind : query.error ? 'failed' : null;
  // 名前が空・削除済みでも一覧の行と同じ表示名にする（空文字でチップと「解除」が消えないように）。
  const filter = configured?.workspaceFilter ?? null;
  const filterName = filter
    ? workspaceLabel({
        workspaceId: filter.workspaceId,
        name: filter.name ?? '',
        slug: filter.slug ?? '',
        deleted: filter.deleted,
      })
    : null;

  return (
    <main className="min-h-screen bg-slate-50 text-slate-900">
      <div className="hidden lg:block">
        <div className="mx-auto max-w-7xl px-6 py-8">
          <header className="mb-6">
            <p className="text-xs uppercase tracking-wider text-slate-500">
              ALYS Internal / admin-ops
            </p>
            <h1 className="mt-1 text-2xl font-semibold text-slate-900">AI 利用</h1>
            <p className="mt-2 text-sm text-slate-600">
              AI
              の呼び出し件数・トークン数と、公開単価による概算原価です。運営内部の数字で、顧客への請求とは関係ありません。
            </p>
          </header>

          {/* 塊 1 — 期間 */}
          <section className="mb-6 flex items-center gap-4">
            <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5">
              {PERIODS.map(([p, label]) => (
                <button
                  key={p}
                  type="button"
                  aria-pressed={p === period}
                  onClick={() => update({ period: p })}
                  className={`rounded px-3 py-1 text-sm ${p === period ? 'bg-slate-900 text-white' : 'text-slate-600 hover:text-slate-900'}`}
                >
                  {label}
                </button>
              ))}
            </div>
            {configured && (
              <p className="text-sm tabular-nums text-slate-600">
                {jstDate(configured.range.from)} 〜 {jstDate(configured.range.to, -1)}（JST）・
                {jstTime(configured.range.asOf)} 時点
              </p>
            )}
          </section>

          {/* 塊 2・3 */}
          <div data-testid="ai-usage-body" aria-busy={switching || query.isLoading}>
            {query.isLoading && (
              <div>
                <p className="text-sm text-slate-500">読み込み中…</p>
                <div className="mt-3 grid grid-cols-4 gap-4">
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="h-20 animate-pulse rounded-lg bg-slate-200/60" />
                  ))}
                </div>
                {/* 内訳は行 skeleton 5 行（State Matrix L1）。0 件の表示（U-13）は取得完了まで出さない。 */}
                <div className="mt-8 space-y-2 rounded-lg border border-slate-200 bg-white p-3">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      data-testid="ai-usage-row-skeleton"
                      className="h-6 animate-pulse rounded bg-slate-200/60"
                    />
                  ))}
                </div>
              </div>
            )}

            {errorKind === 'permission_denied' && (
              <p className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
                AI 利用の記録を読む権限がありません（alys_ops_readonly に SELECT 権限が必要です）。
              </p>
            )}
            {errorKind === 'failed' && (
              <p className="text-sm text-rose-600">
                AI 利用を取得できませんでした。
                <button
                  type="button"
                  onClick={() => void query.refetch()}
                  className="ml-2 underline"
                >
                  再試行
                </button>
              </p>
            )}

            {!errorKind && data && !data.configured && (
              <p className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                admin-ops-api の AI 利用集計が未配線です。
              </p>
            )}

            {!errorKind && configured && (
              <div className={switching ? 'opacity-50 transition-opacity' : undefined}>
                <Totals data={configured} filterName={filterName} />

                <section className="mt-8">
                  <div
                    className="mb-3 flex items-center gap-4 border-b border-slate-200"
                    role="tablist"
                  >
                    {AXES.map(([a, label]) => (
                      <button
                        key={a}
                        type="button"
                        role="tab"
                        aria-selected={a === axis}
                        onClick={() => update({ axis: a })}
                        className={`-mb-px border-b-2 px-1 py-2 text-sm ${a === axis ? 'border-slate-900 font-medium text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  {filterName && (
                    <p className="mb-3 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-700">
                      workspace: {filterName} で絞り込み中
                      <button
                        type="button"
                        onClick={() => update({ workspace: null })}
                        className="text-slate-500 underline"
                      >
                        解除
                      </button>
                    </p>
                  )}
                  <div className="rounded-lg border border-slate-200 bg-white">
                    <AiUsageBreakdown
                      data={configured}
                      axis={axis}
                      sort={sort}
                      onSort={(s) => update({ sort: s === 'cost' ? null : s })}
                      onSelectWorkspace={(w) => update({ workspace: w.workspaceId })}
                    />
                  </div>
                </section>
              </div>
            )}
          </div>

          {/* 塊 4 — 注記（改定日・レートは API が返すので、取れるまでは固定文の行だけ） */}
          {!errorKind && (!data || data.configured) && <Notes data={configured} />}
        </div>
      </div>
      <div className="block lg:hidden">
        <div className="mx-auto max-w-md px-6 py-12 text-center">
          <h1 className="text-lg font-semibold text-slate-900">PC 専用画面</h1>
          <p className="mt-3 text-sm text-slate-600">
            admin-ops は最小 1024px の画面幅でのみ動作します（情報密度が高いため）。 PC
            ブラウザでアクセスしてください。
          </p>
        </div>
      </div>
    </main>
  );
}
